import { Article } from '../../components/Layout'
import { H1, H2, H3, P, Lead, UL, LI, Callout, Code, Tag, Screenshot, Grid, Card, PropTable } from '../../components/DocComponents'

const toc = [
  { id: 'overview', title: 'Overview', level: 2 as const },
  { id: 'table', title: 'Table', level: 2 as const },
  { id: 'list', title: 'List', level: 2 as const },
  { id: 'chart', title: 'Chart', level: 2 as const },
  { id: 'card', title: 'Card', level: 2 as const },
  { id: 'button', title: 'Button', level: 2 as const },
  { id: 'preparation', title: 'Preparation (Interviewer)', level: 2 as const },
  { id: 'common-config', title: 'Common configuration', level: 2 as const },
]

export function ViewTypes() {
  return (
    <Article toc={toc}>
      <H1>View Types</H1>
      <Lead>A View is the fundamental widget in a Plate. Each view has a category that determines how it renders data. Six categories are available.</Lead>

      <H2 id="overview">Overview</H2>
      <Grid cols={3}>
        <Card title="Table" icon="📋">Paginated, sortable rows with configurable columns. Best for structured record lists.</Card>
        <Card title="List" icon="📝">A simple vertical list of records with minimal chrome. Good for compact displays.</Card>
        <Card title="Chart" icon="📈">Bar, line, pie, or area chart. Data comes from a Drinker script.</Card>
        <Card title="Card" icon="🃏">A summary card layout. Best for a single record's key details.</Card>
        <Card title="Button" icon="🔘">A standalone button that navigates to another plate. Used as a call-to-action.</Card>
        <Card title="Preparation" icon="⚙️">Embeds a Preparation Interviewer directly in the plate. The user fills in the form without leaving.</Card>
      </Grid>

      <H2 id="table">Table</H2>
      <P>Displays records as rows in a table. Supports column configuration, pagination, and row-level action buttons.</P>
      <UL>
        <LI>Columns are defined in the view wizard — you can show/hide individual fields and configure link behaviour</LI>
        <LI>Supports the <strong>Coffee API</strong> (cup_list, single, children) and <strong>Drinker</strong> data sources</LI>
        <LI>Pagination is configured separately via a <a href="/docs/plates/input-params" className="text-blue-600 underline underline-offset-2">pagination config</a></LI>
        <LI>Each column can be styled: width, alignment, and a <Code>linkTo</Code> plate for row-level navigation</LI>
      </UL>
      <Screenshot alt="A table view with columns for Name, SKU, Price, and Client, with pagination controls at the bottom" caption="Table views support up to 50 rows per page by default." />

      <H2 id="list">List</H2>
      <P>A simpler alternative to Table. Displays records as a vertical stack. Each item shows the identifier field and one or two additional fields. Use this for compact sidebars or secondary panels.</P>

      <H2 id="chart">Chart</H2>
      <P>Renders data from a <strong>Drinker</strong> as a bar, line, pie, or area chart. The Drinker must return an array of objects with at least one numeric value field. Chart type is configured on the view.</P>
      <Callout type="info">Charts require a Drinker data source. The Coffee API mode does not support charts.</Callout>

      <H2 id="card">Card</H2>
      <P>Displays a single record's fields as a detail card. Best used when the input param resolves to one specific record (Coffee API <Code>single</Code> mode or a Drinker returning a single object).</P>

      <H2 id="button">Button</H2>
      <P>Renders one or more styled buttons that navigate to another plate. The current plate's <Code>runInputs</Code> are forwarded as query params.</P>
      <UL>
        <LI>Set <Code>destinationPlateId</Code> to the target plate</LI>
        <LI>Style the button: background colour, text colour, border, border-radius</LI>
        <LI>Multiple buttons can be added (they render side by side)</LI>
      </UL>

      <H2 id="preparation">Preparation (Interviewer)</H2>
      <P>Embeds a running Preparation Interviewer directly in the plate. When the view loads, Brainer creates a new Journey and immediately presents the first Interviewer step to the user. The user fills in the form and submits — without leaving the plate.</P>
      <UL>
        <LI>Set the <Code>preparationId</Code> to the preparation to run</LI>
        <LI>The initial payload is automatically populated from the plate's current <Code>runInputs</Code></LI>
        <LI>After submission, the view can navigate or update other views via output mappings</LI>
      </UL>
      <Callout type="tip">See <a href="/docs/plates/preparation-views" className="text-blue-600 underline underline-offset-2">Preparation Views</a> for the full configuration guide including output mappings and navigation after completion.</Callout>

      <H2 id="common-config">Common configuration</H2>
      <P>All view types share these settings:</P>
      <PropTable props={[
        { name: 'title', type: 'string', required: true, description: 'Display name shown above the view.' },
        { name: 'category', type: 'enum', required: true, description: 'table | list | chart | card | button | preparation' },
        { name: 'maxHeight', type: 'number', default: '400', description: 'Maximum height in pixels before scrolling.' },
        { name: 'rank', type: 'number', default: '0', description: 'Sort order within the section. Lower = higher up.' },
        { name: 'dataPath', type: 'string', description: 'Dot-notation path inside the result to the data array, e.g. data or result.items. Auto-detected on discovery.' },
        { name: 'backgroundColor', type: 'string', description: 'CSS colour value for the view background.' },
        { name: 'borderColor', type: 'string', description: 'CSS colour value for the view border.' },
        { name: 'borderRadius', type: 'string', description: 'CSS border-radius value, e.g. 8px.' },
      ]} />
    </Article>
  )
}
